// frontend/src/components/tablekit/filters.js

/** Column typing helpers */
export const COL_TYPES = {
    text: 'text',
    number: 'number',
    date: 'date',
  };
  
  export function coerceDate(d) {
    if (!d) return null;
    // Accepts: YYYY-MM-DD, MM-DD-YYYY, or Date.parse()-friendly
    const parts = String(d).split('-').map(x => parseInt(x, 10));
    if (parts.length === 3 && parts[2] >= 1000) {
      const [mm, dd, yyyy] = parts;
      const ts = new Date(yyyy, (mm || 1) - 1, dd || 1).getTime();
      return Number.isNaN(ts) ? null : ts;
    }
    const ts = Date.parse(d);
    return Number.isNaN(ts) ? null : ts;
  }
  
  export function normalizeType(column) {
    const t = (column?.type || '').toLowerCase();
    if (t === COL_TYPES.number) return COL_TYPES.number;
    if (t === COL_TYPES.date) return COL_TYPES.date;
    return COL_TYPES.text;
  }
  
  /** Operator catalogs by type (UI reads from here) */
  export const OPERATORS = {
    [COL_TYPES.text]: [
      { key: 'contains', label: 'contains' },
      { key: 'startsWith', label: 'starts with' },
      { key: 'endsWith', label: 'ends with' },
      { key: 'equals', label: 'equals' },
      { key: 'notEquals', label: 'not equals' },
      { key: 'isEmpty', label: 'is empty' },
      { key: 'isNotEmpty', label: 'is not empty' },
    ],
    [COL_TYPES.number]: [
      { key: 'eq', label: '=' },
      { key: 'ne', label: '≠' },
      { key: 'gt', label: '>' },
      { key: 'gte', label: '≥' },
      { key: 'lt', label: '<' },
      { key: 'lte', label: '≤' },
      { key: 'isEmpty', label: 'is empty' },
      { key: 'isNotEmpty', label: 'is not empty' },
    ],
    [COL_TYPES.date]: [
      { key: 'on', label: 'on' },
      { key: 'before', label: 'before' },
      { key: 'after', label: 'after' },
      { key: 'isEmpty', label: 'is empty' },
      { key: 'isNotEmpty', label: 'is not empty' },
    ],
  };
  
  /** Build a predicate for one filter */
  function predicateForFilter(filter, column) {
    const type = normalizeType(column);
    const acc = column?.accessor;
    const key = column?.key;
  
    const getRaw = (row) => {
      const v = acc ? acc(row) : row[key];
      return v;
    };
  
    // Text
    if (type === COL_TYPES.text) {
      const vStr = (x) => (x == null ? '' : String(x));
      const needle = vStr(filter.value).toLowerCase();
  
      switch (filter.op) {
        case 'contains':
          return (row) => vStr(getRaw(row)).toLowerCase().includes(needle);
        case 'startsWith':
          return (row) => vStr(getRaw(row)).toLowerCase().startsWith(needle);
        case 'endsWith':
          return (row) => vStr(getRaw(row)).toLowerCase().endsWith(needle);
        case 'equals':
          return (row) => vStr(getRaw(row)).toLowerCase() === needle;
        case 'notEquals':
          return (row) => vStr(getRaw(row)).toLowerCase() !== needle;
        case 'isEmpty':
          return (row) => vStr(getRaw(row)).trim() === '';
        case 'isNotEmpty':
          return (row) => vStr(getRaw(row)).trim() !== '';
        default:
          return () => true;
      }
    }
  
    // Number
    if (type === COL_TYPES.number) {
      const vNum = (x) => {
        const n = Number(x);
        return Number.isFinite(n) ? n : null;
      };
      const target = vNum(filter.value);
  
      switch (filter.op) {
        case 'eq':  return (row) => vNum(getRaw(row)) === target;
        case 'ne':  return (row) => vNum(getRaw(row)) !== target;
        case 'gt':  return (row) => {
          const n = vNum(getRaw(row)); return n != null && target != null && n > target;
        };
        case 'gte': return (row) => {
          const n = vNum(getRaw(row)); return n != null && target != null && n >= target;
        };
        case 'lt':  return (row) => {
          const n = vNum(getRaw(row)); return n != null && target != null && n < target;
        };
        case 'lte': return (row) => {
          const n = vNum(getRaw(row)); return n != null && target != null && n <= target;
        };
        case 'isEmpty':    return (row) => vNum(getRaw(row)) == null;
        case 'isNotEmpty': return (row) => vNum(getRaw(row)) != null;
        default: return () => true;
      }
    }
  
    // Date (compare on day resolution)
    if (type === COL_TYPES.date) {
      const day = (ts) => ts ? new Date(ts).setHours(0,0,0,0) : null;
      const target = day(coerceDate(filter.value));
  
      switch (filter.op) {
        case 'on':
          return (row) => {
            const d = day(coerceDate(getRaw(row)));
            return d != null && target != null && d === target;
          };
        case 'before':
          return (row) => {
            const d = day(coerceDate(getRaw(row)));
            return d != null && target != null && d < target;
          };
        case 'after':
          return (row) => {
            const d = day(coerceDate(getRaw(row)));
            return d != null && target != null && d > target;
          };
        case 'isEmpty':    return (row) => coerceDate(getRaw(row)) == null;
        case 'isNotEmpty': return (row) => coerceDate(getRaw(row)) != null;
        default: return () => true;
      }
    }
  
    return () => true;
  }
  
  /** Apply an array of filters to rows */
  export function applyFilters(rows, filters, columns) {
    if (!Array.isArray(filters) || filters.length === 0) return rows;
    const map = new Map(columns.map(c => [c.key, c]));
    const preds = filters
      .map(f => predicateForFilter(f, map.get(f.columnKey)))
      .filter(Boolean);
    if (!preds.length) return rows;
    return rows.filter(r => preds.every(p => p(r)));
  }
  